import { memo, useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import {
  dateLocalToIso,
  dateLocalToIsoEndOfDay,
} from "@/shared/lib/date-format";
import type { GraduationListFilters } from "../hooks/useGraduationList";
import { PaymentMethodChipFilter } from "./PaymentMethodChipFilter";
import { TagMultiSelectField } from "./TagMultiSelectField";

interface GraduationFiltersProps {
  filters: GraduationListFilters;
  loading: boolean;
  onApply: (filters: GraduationListFilters) => void;
  onClear: () => void;
}

const EMPTY_FILTERS: GraduationListFilters = {};

export const GraduationFilters = memo(function GraduationFilters({
  filters,
  loading,
  onApply,
  onClear,
}: GraduationFiltersProps) {
  const [draft, setDraft] = useState<GraduationListFilters>(filters);

  useEffect(() => {
    setDraft(filters);
  }, [filters]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="graduation-filter-event-name">Nome do evento</Label>
          <Input
            id="graduation-filter-event-name"
            placeholder="Ex.: Medicina UFBA"
            value={draft.eventName ?? ""}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                eventName: e.target.value || undefined,
              }))
            }
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="graduation-filter-start-date">Data inicial</Label>
          <Input
            id="graduation-filter-start-date"
            type="date"
            value={draft.startDate ? draft.startDate.slice(0, 10) : ""}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                startDate: e.target.value
                  ? dateLocalToIso(e.target.value)
                  : undefined,
              }))
            }
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="graduation-filter-end-date">Data final</Label>
          <Input
            id="graduation-filter-end-date"
            type="date"
            value={draft.endDate ? draft.endDate.slice(0, 10) : ""}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                endDate: e.target.value
                  ? dateLocalToIsoEndOfDay(e.target.value)
                  : undefined,
              }))
            }
            disabled={loading}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Método de pagamento</Label>
        <PaymentMethodChipFilter
          value={draft.paymentMethod ?? []}
          onChange={(methods) =>
            setDraft((prev) => ({
              ...prev,
              paymentMethod: methods.length > 0 ? methods : undefined,
            }))
          }
          disabled={loading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="graduation-filter-tags">Tags</Label>
        <TagMultiSelectField
          inputId="graduation-filter-tags"
          value={draft.tagIds ?? []}
          onChange={(ids) =>
            setDraft((prev) => ({
              ...prev,
              tagIds: ids.length > 0 ? ids : undefined,
            }))
          }
          disabled={loading}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setDraft(EMPTY_FILTERS);
            onClear();
          }}
          disabled={loading}
          className="gap-2"
        >
          <X className="w-4 h-4" />
          Limpar
        </Button>
        <Button
          onClick={() => onApply(draft)}
          disabled={loading}
          className="gap-2"
        >
          <Search className="w-4 h-4" />
          Aplicar
        </Button>
      </div>
    </div>
  );
});
